import { v4 as uuidv4 } from "uuid";

// Generate stable IDs for demo data (seeded with fixed values)
const generateStableId = (seed: string): string => {
  // Use a simple hash function to generate consistent IDs
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    const char = seed.charCodeAt(i);
    hash = (hash << 5) - hash + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to hex and pad to look like a UUID
  const hex = Math.abs(hash).toString(16).padStart(8, "0");
  return `demo-${hex}-${hex.slice(0, 4)}-4${hex.slice(0, 3)}-${hex.slice(
    0,
    4
  )}-${hex.slice(0, 12).padStart(12, "0")}`;
};

// Helper to get a date in the next month
const getNextMonthDate = (day: number): Date => {
  const now = new Date();
  const lastDayOfMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 2,
    0
  ).getDate();
  const safeDay = Math.min(day, lastDayOfMonth);
  return new Date(now.getFullYear(), now.getMonth() + 1, safeDay);
};

// Helper to get a date two months out
const getTwoMonthsOutDate = (day: number): Date => {
  const now = new Date();
  const lastDayOfMonth = new Date(
    now.getFullYear(),
    now.getMonth() + 3,
    0
  ).getDate();
  const safeDay = Math.min(day, lastDayOfMonth);
  return new Date(now.getFullYear(), now.getMonth() + 2, safeDay);
};

// Helper to get a date in the previous month
const getPreviousMonthDate = (day: number): Date => {
  const now = new Date();
  const lastDayOfPrevMonth = new Date(
    now.getFullYear(),
    now.getMonth(),
    0
  ).getDate();
  const safeDay = Math.min(day, lastDayOfPrevMonth);
  return new Date(now.getFullYear(), now.getMonth() - 1, safeDay);
};

// Helper for relative dates (today-based, time stripped)
const getRelativeDate = (offsetDays: number): Date => {
  const now = new Date();
  const date = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  date.setDate(date.getDate() + offsetDays);
  return date;
};

// Helper to create expense with both formats
const createExpense = (data: any) => ({
  id: data.id || uuidv4(),
  // Both formats for compatibility
  name: data.name,
  amount: data.amount,
  invoicedDate: data.invoicedDate,
  invoiced_date: data.invoicedDate ? data.invoicedDate.toISOString() : null,
  dueDate: data.dueDate,
  due_date: data.dueDate.toISOString(),
  isPaid: data.isPaid,
  is_paid: data.isPaid,
  isRecurring: data.isRecurring,
  is_recurring: data.isRecurring,
  recurrencePattern: data.recurrencePattern,
  recurrence_pattern: data.recurrencePattern,
  vendor: data.vendor,
  internalCategory: data.internalCategory,
  internal_category: data.internalCategory,
  paymentAccount: data.paymentAccount,
  payment_account: data.paymentAccount,
  paymentDate: data.paymentDate,
  payment_date: data.paymentDate ? data.paymentDate.toISOString() : null,
  linkedDebtId: data.linkedDebtId,
  linked_debt_id: data.linkedDebtId,
  linkedTransactionId: data.linkedTransactionId,
  linked_transaction_id: data.linkedTransactionId,
  notes: data.notes,
  created_at: new Date("2025-01-01").toISOString(),
  updated_at: new Date().toISOString(),
});

// Demo Expenses Data - Using dynamic dates for current month
export const demoExpenses = [
  createExpense({
    name: "Rent/Mortgage",
    amount: 2500.0,
    dueDate: getPreviousMonthDate(28),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Property Management LLC",
    internalCategory: "Housing",
    paymentAccount: "Chase Checking",
    paymentDate: null,
  }),
  createExpense({
    name: "Electricity",
    amount: 156.42,
    dueDate: getRelativeDate(0),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "City Electric Co",
    internalCategory: "Utilities",
    paymentAccount: undefined,
    paymentDate: null,
  }),
  createExpense({
    name: "Internet & Cable",
    amount: 129.99,
    dueDate: getRelativeDate(1),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Comcast",
    internalCategory: "Utilities",
    paymentAccount: "Chase Checking",
    paymentDate: null,
  }),
  createExpense({
    name: "Cell Phone",
    amount: 85.0,
    dueDate: getRelativeDate(3),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Verizon",
    internalCategory: "Utilities",
    paymentAccount: "Chase Credit Card",
    paymentDate: null,
  }),
  createExpense({
    name: "Car Insurance",
    amount: 185.5,
    dueDate: getRelativeDate(12),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "State Farm",
    internalCategory: "Insurance",
    paymentAccount: undefined,
    paymentDate: null,
  }),
  createExpense({
    name: "Health Insurance",
    amount: 450.0,
    dueDate: getNextMonthDate(5),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Blue Cross Blue Shield",
    internalCategory: "Insurance",
    paymentAccount: "HSA Account",
    paymentDate: null,
  }),
  createExpense({
    name: "Groceries",
    amount: 650.0,
    dueDate: getRelativeDate(-2),
    isPaid: false,
    isRecurring: false,
    vendor: "Various Stores",
    internalCategory: "Food",
    paymentAccount: "Chase Credit Card",
    paymentDate: null,
  }),
  createExpense({
    name: "Netflix",
    amount: 15.99,
    dueDate: getRelativeDate(18),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Netflix Inc",
    internalCategory: "Entertainment",
    paymentAccount: "Chase Credit Card",
    paymentDate: null,
  }),
  createExpense({
    name: "Gym Membership",
    amount: 49.99,
    dueDate: getTwoMonthsOutDate(1),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Planet Fitness",
    internalCategory: "Health",
    paymentAccount: "Chase Checking",
    paymentDate: null,
  }),
  createExpense({
    name: "Gas",
    amount: 180.0,
    dueDate: getNextMonthDate(15),
    isPaid: false,
    isRecurring: false,
    vendor: "Shell/Chevron",
    internalCategory: "Transportation",
    paymentAccount: undefined,
    paymentDate: null,
  }),
  createExpense({
    name: "Spotify Premium",
    amount: 10.99,
    dueDate: getRelativeDate(6),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "Spotify",
    internalCategory: "Entertainment",
    paymentAccount: "Chase Credit Card",
    paymentDate: null,
  }),
  createExpense({
    name: "Water Bill",
    amount: 65.0,
    dueDate: getTwoMonthsOutDate(10),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    vendor: "City Water Dept",
    internalCategory: "Utilities",
    paymentAccount: undefined,
    paymentDate: null,
  }),
];

// Helper to create income with both formats
const createIncome = (data: any) => ({
  id: data.id || uuidv4(),
  client: data.client,
  invoiceNumber: data.invoiceNumber,
  invoice_number: data.invoiceNumber,
  invoiceLink: data.invoiceLink,
  invoice_link: data.invoiceLink,
  invoiceTotal: data.invoiceTotal,
  invoice_total: data.invoiceTotal,
  paidToDate: data.paidToDate,
  paid_to_date: data.paidToDate,
  expectedByDate: data.expectedByDate,
  expected_by_date: data.expectedByDate.toISOString(),
  dependsOnCompletion: data.dependsOnCompletion,
  depends_on_completion: data.dependsOnCompletion,
  isPaid: data.isPaid,
  is_paid: data.isPaid,
  isRecurring: data.isRecurring,
  is_recurring: data.isRecurring,
  recurrencePattern: data.recurrencePattern,
  recurrence_pattern: data.recurrencePattern,
  notes: data.notes,
  receivedDate: data.receivedDate,
  received_date: data.receivedDate ? data.receivedDate.toISOString() : null,
  paymentProcessor: data.paymentProcessor,
  payment_processor: data.paymentProcessor,
  businessCategory: data.businessCategory,
  business_category: data.businessCategory,
  created_at: new Date("2025-01-01").toISOString(),
  updated_at: new Date().toISOString(),
});

// Demo Income Data - Using dynamic dates for current month
const currentYear = new Date().getFullYear();
const invoicePrefix = `INV-${currentYear}-`;

export const demoIncome = [
  createIncome({
    client: "Tech Innovations Corp",
    invoiceNumber: `${invoicePrefix}001`,
    invoiceLink: "https://invoice.example.com/001",
    invoiceTotal: 12500.0,
    paidToDate: 12500.0,
    expectedByDate: getPreviousMonthDate(20),
    isPaid: true,
    isRecurring: false,
    receivedDate: getPreviousMonthDate(19),
    paymentProcessor: "ACH Transfer",
    businessCategory: "Software Development",
  }),
  createIncome({
    client: "StartupXYZ",
    invoiceNumber: `${invoicePrefix}002`,
    invoiceLink: "https://invoice.example.com/002",
    invoiceTotal: 7500.0,
    paidToDate: 0,
    expectedByDate: getRelativeDate(0),
    dependsOnCompletion: "Phase 2 Delivery",
    isPaid: false,
    isRecurring: false,
    receivedDate: null,
    paymentProcessor: "PayPal",
    businessCategory: "Consulting",
  }),
  createIncome({
    client: "Monthly Retainer Co",
    invoiceNumber: `${invoicePrefix}003`,
    invoiceLink: "https://invoice.example.com/003",
    invoiceTotal: 5000.0,
    paidToDate: 0,
    expectedByDate: getRelativeDate(1),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    receivedDate: null,
    paymentProcessor: "Stripe",
    businessCategory: "Support & Maintenance",
  }),
  createIncome({
    client: "E-commerce Solutions",
    invoiceNumber: `${invoicePrefix}004`,
    invoiceLink: "https://invoice.example.com/004",
    invoiceTotal: 3250.0,
    paidToDate: 3250.0,
    expectedByDate: getRelativeDate(-10),
    isPaid: true,
    isRecurring: false,
    receivedDate: getRelativeDate(-9),
    paymentProcessor: "Wire Transfer",
    businessCategory: "Web Development",
  }),
  createIncome({
    client: "Digital Marketing Agency",
    invoiceNumber: `${invoicePrefix}005`,
    invoiceLink: "https://invoice.example.com/005",
    invoiceTotal: 2000.0,
    paidToDate: 0,
    expectedByDate: getPreviousMonthDate(12),
    isPaid: false,
    isRecurring: true,
    recurrencePattern: "monthly",
    receivedDate: null,
    paymentProcessor: "ACH Transfer",
    businessCategory: "SEO Services",
  }),
  createIncome({
    client: "Cloud Solutions Inc",
    invoiceNumber: `${invoicePrefix}006`,
    invoiceLink: "https://invoice.example.com/006",
    invoiceTotal: 4500.0,
    paidToDate: 0,
    expectedByDate: getRelativeDate(4),
    isPaid: false,
    isRecurring: false,
    receivedDate: null,
    paymentProcessor: "ACH Transfer",
    businessCategory: "Cloud Infrastructure",
  }),
  createIncome({
    client: "Mobile App Client",
    invoiceNumber: `${invoicePrefix}007`,
    invoiceLink: "https://invoice.example.com/007",
    invoiceTotal: 8000.0,
    paidToDate: 0,
    expectedByDate: getNextMonthDate(8),
    dependsOnCompletion: "App Store Approval",
    isPaid: false,
    isRecurring: false,
    receivedDate: null,
    paymentProcessor: "Wire Transfer",
    businessCategory: "Mobile Development",
  }),
  createIncome({
    client: "Long-Term Partnership LLC",
    invoiceNumber: `${invoicePrefix}008`,
    invoiceLink: "https://invoice.example.com/008",
    invoiceTotal: 12000.0,
    paidToDate: 0,
    expectedByDate: getTwoMonthsOutDate(12),
    isPaid: false,
    isRecurring: false,
    receivedDate: null,
    paymentProcessor: "ACH Transfer",
    businessCategory: "Product Strategy",
  }),
];

// Helper to create debt with both formats
const createDebt = (data: any) => ({
  id: data.id || uuidv4(),
  account: data.account,
  type: data.type,
  balance: data.balance,
  creditLimit: data.creditLimit,
  credit_limit: data.creditLimit,
  paydownAmount: data.paydownAmount,
  paydown_amount: data.paydownAmount,
  maxMonthlyPayment: data.maxMonthlyPayment,
  max_monthly_payment: data.maxMonthlyPayment,
  dueDate: data.dueDate,
  due_date: data.dueDate,
  status: data.status,
  creditAffecting: data.creditAffecting,
  credit_affecting: data.creditAffecting,
  payoffStrategy: data.payoffStrategy,
  payoff_strategy: data.payoffStrategy,
  stateCategory: data.stateCategory,
  state_category: data.stateCategory,
  statementCredits: data.statementCredits,
  statement_credits: data.statementCredits,
  linkedExpenseId: data.linkedExpenseId,
  linked_expense_id: data.linkedExpenseId,
  created_at: new Date("2025-01-01").toISOString(),
  updated_at: new Date().toISOString(),
});

// Demo Debts Data
export const demoDebts = [
  createDebt({
    account: "Chase Sapphire Credit Card",
    type: "Credit Card",
    balance: 4567.89,
    creditLimit: 15000.0,
    paydownAmount: 500.0,
    maxMonthlyPayment: 1500.0,
    dueDate: "15th of each month",
    status: "Active",
    creditAffecting: "Yes",
    payoffStrategy: "Avalanche Method",
    stateCategory: "High Priority",
  }),
  createDebt({
    account: "Student Loan - Federal",
    type: "Student Loan",
    balance: 23456.78,
    paydownAmount: 350.0,
    maxMonthlyPayment: 350.0,
    dueDate: "1st of each month",
    status: "Active",
    creditAffecting: "Yes",
    payoffStrategy: "Standard Repayment",
    stateCategory: "Regular Payment",
  }),
  createDebt({
    account: "Auto Loan - Toyota Camry",
    type: "Auto Loan",
    balance: 12345.67,
    paydownAmount: 425.0,
    maxMonthlyPayment: 425.0,
    dueDate: "20th of each month",
    status: "Active",
    creditAffecting: "Yes",
    payoffStrategy: "Regular Payments",
    stateCategory: "Regular Payment",
  }),
  createDebt({
    account: "Home Equity Line of Credit",
    type: "HELOC",
    balance: 35000.0,
    creditLimit: 50000.0,
    paydownAmount: 800.0,
    maxMonthlyPayment: 2000.0,
    dueDate: "5th of each month",
    status: "Active",
    creditAffecting: "Yes",
    payoffStrategy: "Extra Principal Payments",
    stateCategory: "Medium Priority",
  }),
  createDebt({
    account: "Personal Loan - Consolidation",
    type: "Personal Loan",
    balance: 8900.0,
    paydownAmount: 275.0,
    maxMonthlyPayment: 275.0,
    dueDate: "10th of each month",
    status: "Active",
    creditAffecting: "Yes",
    payoffStrategy: "Fixed Payments",
    stateCategory: "Regular Payment",
  }),
];

// Fixed account IDs (must be defined before transactions that reference them)
const ACCOUNT_IDS = {
  checking: "demo-checking-001",
  savings: "demo-savings-001",
  credit: "demo-credit-001",
  investment: "demo-investment-001",
  hsa: "demo-hsa-001",
};

// Demo Accounts Data (for Plaid-like accounts)
export const demoAccounts = [
  {
    id: ACCOUNT_IDS.checking,
    name: "Chase Checking",
    type: "depository",
    subtype: "checking",
    mask: "1234",
    balance: 5432.1,
    available_balance: 5432.1,
    institution: "Chase Bank",
    created_at: new Date("2025-01-01").toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: ACCOUNT_IDS.savings,
    name: "Chase Savings",
    type: "depository",
    subtype: "savings",
    mask: "5678",
    balance: 15678.9,
    available_balance: 15678.9,
    institution: "Chase Bank",
    created_at: new Date("2025-01-01").toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: ACCOUNT_IDS.credit,
    name: "Chase Sapphire Credit Card",
    type: "credit",
    subtype: "credit card",
    mask: "9012",
    balance: 4567.89,
    credit_limit: 15000.0,
    available_credit: 10432.11,
    institution: "Chase Bank",
    created_at: new Date("2025-01-01").toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: ACCOUNT_IDS.investment,
    name: "Fidelity Investment",
    type: "investment",
    subtype: "brokerage",
    mask: "3456",
    balance: 45678.9,
    available_balance: 45678.9,
    institution: "Fidelity",
    created_at: new Date("2025-01-01").toISOString(),
    updated_at: new Date().toISOString(),
  },
  {
    id: ACCOUNT_IDS.hsa,
    name: "HSA Account",
    type: "depository",
    subtype: "hsa",
    mask: "7890",
    balance: 3456.78,
    available_balance: 3456.78,
    institution: "Health Savings Bank",
    created_at: new Date("2025-01-01").toISOString(),
    updated_at: new Date().toISOString(),
  },
];

// Helper function to generate dates over past few months
const generateDate = (daysAgo: number) => {
  const date = new Date();
  date.setDate(date.getDate() - daysAgo);
  return date.toISOString().split("T")[0]; // Return YYYY-MM-DD string format
};

// Demo Transactions (for transaction history) - More comprehensive Plaid-like data
export const demoTransactions = [
  // Recent transactions
  {
    id: generateStableId("txn-1-property-management-llc"),
    account_id: ACCOUNT_IDS.checking,
    amount: 2500.0,
    date: generateDate(3),
    name: "Property Management LLC",
    category: ["Housing", "Rent"],
    pending: false,
    merchant_name: "Property Management LLC",
    payment_method: "ACH",
    created_at: new Date(generateDate(3)).toISOString(),
  },
  {
    id: generateStableId("txn-2-whole-foods-market"),
    account_id: ACCOUNT_IDS.credit,
    amount: 127.45,
    date: generateDate(4),
    name: "Whole Foods Market",
    category: ["Food and Drink", "Groceries"],
    pending: false,
    merchant_name: "Whole Foods",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(4)).toISOString(),
  },
  {
    id: generateStableId("txn-3-shell-gas-station"),
    account_id: ACCOUNT_IDS.credit,
    amount: 65.0,
    date: generateDate(5),
    name: "Shell Gas Station",
    category: ["Transportation", "Gas"],
    pending: false,
    merchant_name: "Shell",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(5)).toISOString(),
  },
  {
    id: generateStableId("txn-4-tech-innovations-corp---payment"),
    account_id: ACCOUNT_IDS.checking,
    amount: -12500.0,
    date: generateDate(4),
    name: "Tech Innovations Corp - Payment",
    category: ["Transfer", "Deposit"],
    pending: false,
    merchant_name: "Tech Innovations Corp",
    payment_method: "ACH",
    created_at: new Date(generateDate(4)).toISOString(),
  },
  {
    id: generateStableId("txn-5-verizon-wireless"),
    account_id: ACCOUNT_IDS.checking,
    amount: 85.0,
    date: generateDate(2),
    name: "Verizon Wireless",
    category: ["Service", "Telecommunication Services"],
    pending: false,
    merchant_name: "Verizon",
    payment_method: "Auto Pay",
    created_at: new Date(generateDate(2)).toISOString(),
  },
  // Additional realistic transactions
  {
    id: generateStableId("txn-6-target"),
    account_id: ACCOUNT_IDS.credit,
    amount: 42.99,
    date: generateDate(6),
    name: "Target",
    category: ["Shops", "Supermarkets and Groceries"],
    pending: false,
    merchant_name: "Target",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(6)).toISOString(),
  },
  {
    id: generateStableId("txn-7-city-electric-co"),
    account_id: ACCOUNT_IDS.credit,
    amount: 156.42,
    date: generateDate(7),
    name: "City Electric Co",
    category: ["Service", "Utilities"],
    pending: false,
    merchant_name: "City Electric",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(7)).toISOString(),
  },
  {
    id: generateStableId("txn-8-netflix"),
    account_id: ACCOUNT_IDS.checking,
    amount: 15.99,
    date: generateDate(8),
    name: "Netflix",
    category: ["Service", "Subscription"],
    pending: false,
    merchant_name: "Netflix",
    payment_method: "Auto Pay",
    created_at: new Date(generateDate(8)).toISOString(),
  },
  {
    id: generateStableId("txn-9-amazon-com"),
    account_id: ACCOUNT_IDS.credit,
    amount: 89.5,
    date: generateDate(9),
    name: "Amazon.com",
    category: ["Shops", "E-commerce"],
    pending: false,
    merchant_name: "Amazon",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(9)).toISOString(),
  },
  {
    id: generateStableId("txn-10-planet-fitness"),
    account_id: ACCOUNT_IDS.checking,
    amount: 49.99,
    date: generateDate(10),
    name: "Planet Fitness",
    category: ["Recreation", "Gyms and Fitness Centers"],
    pending: false,
    merchant_name: "Planet Fitness",
    payment_method: "Auto Pay",
    created_at: new Date(generateDate(10)).toISOString(),
  },
  {
    id: generateStableId("txn-11-starbucks"),
    account_id: ACCOUNT_IDS.credit,
    amount: 32.18,
    date: generateDate(11),
    name: "Starbucks",
    category: ["Food and Drink", "Coffee Shop"],
    pending: false,
    merchant_name: "Starbucks",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(11)).toISOString(),
  },
  {
    id: generateStableId("txn-12-state-farm-insurance"),
    account_id: ACCOUNT_IDS.checking,
    amount: 185.5,
    date: generateDate(12),
    name: "State Farm Insurance",
    category: ["Service", "Insurance"],
    pending: false,
    merchant_name: "State Farm",
    payment_method: "ACH",
    created_at: new Date(generateDate(12)).toISOString(),
  },
  {
    id: generateStableId("txn-13-uber"),
    account_id: ACCOUNT_IDS.credit,
    amount: 23.99,
    date: generateDate(13),
    name: "Uber",
    category: ["Transportation", "Taxi"],
    pending: false,
    merchant_name: "Uber",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(13)).toISOString(),
  },
  {
    id: generateStableId("txn-14-monthly-retainer-co---payment"),
    account_id: ACCOUNT_IDS.checking,
    amount: -5000.0,
    date: generateDate(15),
    name: "Monthly Retainer Co - Payment",
    category: ["Transfer", "Deposit"],
    pending: false,
    merchant_name: "Monthly Retainer Co",
    payment_method: "Wire Transfer",
    created_at: new Date(generateDate(15)).toISOString(),
  },
  {
    id: generateStableId("txn-15-chevron"),
    account_id: ACCOUNT_IDS.credit,
    amount: 78.9,
    date: generateDate(16),
    name: "Chevron",
    category: ["Transportation", "Gas"],
    pending: false,
    merchant_name: "Chevron",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(16)).toISOString(),
  },
  {
    id: generateStableId("txn-16-comcast-internet"),
    account_id: ACCOUNT_IDS.checking,
    amount: 129.99,
    date: generateDate(17),
    name: "Comcast Internet",
    category: ["Service", "Internet and Cable"],
    pending: false,
    merchant_name: "Comcast",
    payment_method: "Auto Pay",
    created_at: new Date(generateDate(17)).toISOString(),
  },
  {
    id: generateStableId("txn-17-costco"),
    account_id: ACCOUNT_IDS.credit,
    amount: 215.0,
    date: generateDate(20),
    name: "Costco",
    category: ["Shops", "Supermarkets and Groceries"],
    pending: false,
    merchant_name: "Costco",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(20)).toISOString(),
  },
  {
    id: generateStableId("txn-18-toyota-financial---car-payment"),
    account_id: ACCOUNT_IDS.checking,
    amount: 425.0,
    date: generateDate(22),
    name: "Toyota Financial - Car Payment",
    category: ["Payment", "Credit Card"],
    pending: false,
    merchant_name: "Toyota Financial",
    payment_method: "ACH",
    created_at: new Date(generateDate(22)).toISOString(),
  },
  {
    id: generateStableId("txn-19-chipotle-mexican-grill"),
    account_id: ACCOUNT_IDS.credit,
    amount: 18.5,
    date: generateDate(25),
    name: "Chipotle Mexican Grill",
    category: ["Food and Drink", "Restaurants"],
    pending: false,
    merchant_name: "Chipotle",
    payment_method: "Credit Card",
    created_at: new Date(generateDate(25)).toISOString(),
  },
  {
    id: generateStableId("txn-20-e-commerce-solutions---payment"),
    account_id: ACCOUNT_IDS.checking,
    amount: -3250.0,
    date: generateDate(28),
    name: "E-commerce Solutions - Payment",
    category: ["Transfer", "Deposit"],
    pending: false,
    merchant_name: "E-commerce Solutions",
    payment_method: "ACH",
    created_at: new Date(generateDate(28)).toISOString(),
  },
  // Older transactions (1 month ago)
  {
    id: generateStableId("txn-21-property-management-llc"),
    account_id: ACCOUNT_IDS.checking,
    amount: 2500.0,
    date: generateDate(33),
    name: "Property Management LLC",
    category: ["Housing", "Rent"],
    pending: false,
    merchant_name: "Property Management LLC",
    payment_method: "ACH",
    created_at: new Date(generateDate(33)).toISOString(),
  },
  {
    id: generateStableId("txn-22-blue-cross-blue-shield"),
    account_id: ACCOUNT_IDS.hsa,
    amount: 450.0,
    date: generateDate(35),
    name: "Blue Cross Blue Shield",
    category: ["Service", "Insurance"],
    pending: false,
    merchant_name: "BCBS",
    payment_method: "HSA",
    created_at: new Date(generateDate(35)).toISOString(),
  },
  {
    id: generateStableId("txn-23-transfer-to-savings"),
    account_id: ACCOUNT_IDS.checking,
    amount: 1000.0,
    date: generateDate(40),
    name: "Transfer to Savings",
    category: ["Transfer", "Internal Account Transfer"],
    pending: false,
    merchant_name: "Chase Bank",
    payment_method: "Transfer",
    created_at: new Date(generateDate(40)).toISOString(),
  },
  {
    id: generateStableId("txn-24-transfer-from-checking"),
    account_id: ACCOUNT_IDS.savings,
    amount: -1000.0,
    date: generateDate(40),
    name: "Transfer from Checking",
    category: ["Transfer", "Internal Account Transfer"],
    pending: false,
    merchant_name: "Chase Bank",
    payment_method: "Transfer",
    created_at: new Date(generateDate(40)).toISOString(),
  },
];

// Helper to create goal with proper typing
const createGoal = (data: any) => ({
  id: data.id || uuidv4(),
  name: data.name,
  description: data.description,
  targetAmount: data.targetAmount,
  target_amount: data.targetAmount,
  currentAmount: data.currentAmount,
  current_amount: data.currentAmount,
  targetDate: data.targetDate,
  target_date: data.targetDate.toISOString(),
  icon: data.icon,
  status: data.status,
  linkedAccountIds: data.linkedAccountIds || [],
  linked_account_ids: data.linkedAccountIds || [],
  createdAt: new Date("2025-01-01"),
  created_at: new Date("2025-01-01").toISOString(),
  updatedAt: new Date(),
  updated_at: new Date().toISOString(),
});

// Demo Goals Data
export const demoGoals = [
  createGoal({
    name: "Emergency Fund",
    description: "6 months of living expenses for emergencies",
    targetAmount: 25000,
    currentAmount: 15678.9, // Linked to savings account balance
    targetDate: new Date(new Date().getFullYear() + 1, 5, 30), // June next year
    icon: "emergency",
    status: "active",
    linkedAccountIds: [ACCOUNT_IDS.savings],
  }),
  createGoal({
    name: "New Car Down Payment",
    description: "Save for 20% down payment on a new vehicle",
    targetAmount: 8000,
    currentAmount: 3200,
    targetDate: new Date(new Date().getFullYear() + 1, 11, 31), // Dec next year
    icon: "car",
    status: "active",
    linkedAccountIds: [],
  }),
  createGoal({
    name: "Hawaii Vacation",
    description: "Family trip to Maui for 10 days",
    targetAmount: 6000,
    currentAmount: 2100,
    targetDate: new Date(new Date().getFullYear() + 1, 2, 15), // March next year
    icon: "vacation",
    status: "active",
    linkedAccountIds: [],
  }),
  createGoal({
    name: "Home Down Payment",
    description: "Saving for 20% down payment on a house",
    targetAmount: 80000,
    currentAmount: 45678.9, // Linked to investment account
    targetDate: new Date(new Date().getFullYear() + 3, 0, 1), // 3 years out
    icon: "home",
    status: "active",
    linkedAccountIds: [ACCOUNT_IDS.investment],
  }),
  createGoal({
    name: "Kids College Fund",
    description: "Education savings for children",
    targetAmount: 50000,
    currentAmount: 12500,
    targetDate: new Date(new Date().getFullYear() + 10, 8, 1), // 10 years out
    icon: "education",
    status: "active",
    linkedAccountIds: [],
  }),
];

// Helper to get all demo data
export const getAllDemoData = () => ({
  expenses: demoExpenses,
  income: demoIncome,
  debts: demoDebts,
  accounts: demoAccounts,
  transactions: demoTransactions,
  goals: demoGoals,
});
